// F8 stuck-trace capture: the last ten seconds of predicted movement, for reproducing "I got stuck
// here" reports. `LocalPlayer` writes one row per predicted tick and one per reconciliation that moved
// the camera into rings of preallocated rows (no serialization, no DOM, nothing but field writes).
// Only F8 turns the rings into JSON and POSTs it to `/dev/trace` of a `--dev` server.

import { isRecord } from "../app/typeGuards";
import { TICK_RATE, wholeTicks } from "../net/constants";
import type { TransportKind } from "../net/transport";
import { Buttons } from "../wasm/types";
import type { InputCmdData, MoveEventsData, PlayerView } from "../wasm/types";

/** 3: ticks are 1/120 s and `slack` is in 1/960 s (protocol 5); the payload names its `tickRate`. */
export const TRACE_VERSION = 3;
export const TRACE_NAME = "f8";
/** Same origin as the page; the Vite dev server proxies it to the game server (client/vite.config.ts). */
export const TRACE_URL = "/dev/trace";
/** Ten seconds of prediction. */
export const TRACE_TICKS = wholeTicks(10);
/** One reconciliation per snapshot (at most one per tick), so this covers the same window. */
export const TRACE_CORRECTIONS = TRACE_TICKS;
/** One row per snapshot (one per tick), the same ten seconds. */
export const TRACE_SNAPSHOTS = TRACE_TICKS;
const POST_TIMEOUT_MS = 10_000;

// ---- Move events ----

type MoveFlagName = {
  [K in keyof MoveEventsData]: MoveEventsData[K] extends boolean ? K : never;
}[keyof MoveEventsData];

/**
 * The boolean fields of `MoveEventsData`. Typed as a record so that adding one to the interface is a
 * compile error here until it is named: a trace must never silently miss an event.
 */
const MOVE_EVENT_FLAGS: Record<MoveFlagName, true> = {
  jumped: true,
  slideStarted: true,
  slideReset: true,
  wallrunStarted: true,
  wallJumped: true,
  climbStarted: true,
  mantled: true,
  ledgeRecovered: true,
  jumpPad: true,
  perfectHop: true,
};

/** Bit `i` of `TickRow.eventBits` is `MOVE_EVENT_NAMES[i]`. */
export const MOVE_EVENT_NAMES = Object.keys(MOVE_EVENT_FLAGS) as MoveFlagName[];

/** Names of the events set in `bits` (`TickRow.eventBits`). */
export function eventNames(bits: number): string[] {
  const out: string[] = [];
  for (let i = 0; i < MOVE_EVENT_NAMES.length; i++) {
    if ((bits & (1 << i)) !== 0) out.push(MOVE_EVENT_NAMES[i]);
  }
  return out;
}

// ---- Ring ----

/**
 * Fixed-capacity ring of preallocated rows. `claim()` hands out the oldest row for overwriting, so a
 * full ring allocates nothing: the caller only assigns the fields of the row it gets.
 */
export class Ring<T> {
  private readonly items: T[] = [];
  /** Index `claim()` returns next. */
  private head = 0;
  private count = 0;

  constructor(
    readonly capacity: number,
    make: () => T,
  ) {
    for (let i = 0; i < capacity; i++) this.items.push(make());
  }

  get length(): number {
    return this.count;
  }

  /** The row to fill next: a fresh one while the ring has room, afterwards the oldest (its content is lost). */
  claim(): T {
    const row = this.items[this.head] as T;
    if (++this.head === this.capacity) this.head = 0;
    if (this.count < this.capacity) this.count++;
    return row;
  }

  /** The `i`-th row, 0 = oldest, `length - 1` = newest. */
  at(i: number): T {
    let k = (this.count < this.capacity ? 0 : this.head) + i;
    if (k >= this.capacity) k -= this.capacity;
    return this.items[k] as T;
  }

  /** Rows from the oldest to the newest, mapped through `fn`. */
  map<U>(fn: (row: T) => U): U[] {
    const out: U[] = [];
    for (let i = 0; i < this.count; i++) out.push(fn(this.at(i)));
    return out;
  }

  clear(): void {
    this.head = 0;
    this.count = 0;
  }
}

// ---- Rows ----

type Vec3Tuple = [number, number, number];

export interface TickFlags {
  onGround: boolean;
  sliding: boolean;
  crouched: boolean;
  /** -1 wall on the left, 1 wall on the right, 0 not wall running. */
  wallrunSide: number;
  climbing: boolean;
  mantling: boolean;
}

export interface TickInput {
  /** +1 forward, -1 back, 0 none or both. */
  fwd: number;
  /** +1 right, -1 left, 0 none or both. */
  right: number;
  jump: boolean;
  crouch: boolean;
  walk: boolean;
  /** Quantized view yaw as sent to the server (u16, `quantizeYaw`). */
  yaw: number;
  /** Quantized view pitch as sent to the server (u16, `quantizePitch`). */
  pitch: number;
}

/** A ring row of one predicted tick. Fields are overwritten in place, never reallocated. */
export interface TickRow {
  tick: number;
  /** `performance.now()` of the frame that simulated the tick (ticks of one frame share it). */
  t: number;
  pos: Vec3Tuple;
  vel: Vec3Tuple;
  /** Horizontal speed (m/s). */
  speed: number;
  flags: TickFlags;
  input: TickInput;
  /** Bit `i` set: `MOVE_EVENT_NAMES[i]` happened this tick. */
  eventBits: number;
  /** Speed into the ground (m/s) when the player touched down this tick, else null. */
  landed: number | null;
  /** The server had already confirmed this tick when the client reached it: nothing was simulated (`Predictor.step`), the row repeats the unchanged state. */
  settled: boolean;
}

/** A ring row of one reconciliation that corrected the prediction. */
export interface CorrectionRow {
  /** Latest predicted tick after the reconciliation. */
  tick: number;
  t: number;
  /** `old_latest_pos - new_latest_pos`: what `camera.correct` received (m). */
  err: Vec3Tuple;
  /** Tick of the snapshot that was reconciled against. */
  serverTick: number;
  /** Newest input tick the server had from us when it built the snapshot (`serverTick + inputLead`). */
  ackTick: number;
  /** Smoothed round-trip time, null before the first pong. */
  rttMs: number | null;
}

/** What a snapshot did to the prediction. */
export const SNAPSHOT_OUTCOME = { match: 0, corrected: 1, adopted: 2 } as const;

/** A ring row of one snapshot that reached the local player (the lead and the reconciliation, whether or not it corrected). */
export interface SnapshotRow {
  /** `performance.now()` when the snapshot was processed. */
  t: number;
  /** Tick of the snapshot. */
  tick: number;
  /** `inputLead` of the snapshot: newest input tick the server had minus `tick`. */
  lead: number;
  /** `inputSlack` in 1/960 s (`SLACK_PER_TICK` per tick), null when no input arrived since the previous snapshot. */
  slack: number | null;
  /** Newest predicted tick before the reconciliation (below `tick`: the client had not simulated it yet). */
  latestBefore: number;
  /** `SNAPSHOT_OUTCOME` value. */
  outcome: number;
  /** Length of the correction (m), 0 unless corrected. */
  err: number;
}

function newTickRow(): TickRow {
  return {
    tick: 0,
    t: 0,
    pos: [0, 0, 0],
    vel: [0, 0, 0],
    speed: 0,
    flags: { onGround: false, sliding: false, crouched: false, wallrunSide: 0, climbing: false, mantling: false },
    input: { fwd: 0, right: 0, jump: false, crouch: false, walk: false, yaw: 0, pitch: 0 },
    eventBits: 0,
    landed: null,
    settled: false,
  };
}

function newCorrectionRow(): CorrectionRow {
  return { tick: 0, t: 0, err: [0, 0, 0], serverTick: 0, ackTick: 0, rttMs: null };
}

function newSnapshotRow(): SnapshotRow {
  return { t: 0, tick: 0, lead: 0, slack: null, latestBefore: 0, outcome: 0, err: 0 };
}

/** Writes one predicted tick into `row`: only field assignments. */
export function fillTickRow(row: TickRow, nowMs: number, view: PlayerView, ev: MoveEventsData, cmd: InputCmdData, settled = false): void {
  row.tick = view.tick;
  row.t = nowMs;
  row.pos[0] = view.pos.x;
  row.pos[1] = view.pos.y;
  row.pos[2] = view.pos.z;
  row.vel[0] = view.vel.x;
  row.vel[1] = view.vel.y;
  row.vel[2] = view.vel.z;
  row.speed = view.speed;
  const f = row.flags;
  f.onGround = view.onGround;
  f.sliding = view.sliding;
  f.crouched = view.crouched;
  f.wallrunSide = view.wallrunSide;
  f.climbing = view.climbing;
  f.mantling = view.mantling;
  const b = cmd.buttons;
  const i = row.input;
  i.fwd = ((b & Buttons.FORWARD) !== 0 ? 1 : 0) - ((b & Buttons.BACK) !== 0 ? 1 : 0);
  i.right = ((b & Buttons.RIGHT) !== 0 ? 1 : 0) - ((b & Buttons.LEFT) !== 0 ? 1 : 0);
  i.jump = (b & Buttons.JUMP) !== 0;
  i.crouch = (b & Buttons.CROUCH) !== 0;
  i.walk = (b & Buttons.WALK) !== 0;
  i.yaw = cmd.yaw;
  i.pitch = cmd.pitch;
  let bits = 0;
  for (let k = 0; k < MOVE_EVENT_NAMES.length; k++) {
    if (ev[MOVE_EVENT_NAMES[k]]) bits |= 1 << k;
  }
  row.eventBits = bits;
  row.landed = ev.landed;
  row.settled = settled;
}

// ---- Recorder ----

export interface TraceSession {
  map: string;
  room: string;
  /** The local player's id (slot) in the room. */
  slot: number;
  /** The transport of the connection ("" before one exists). */
  transport: TransportKind | "";
}

export class TraceRecorder {
  readonly ticks = new Ring<TickRow>(TRACE_TICKS, newTickRow);
  readonly corrections = new Ring<CorrectionRow>(TRACE_CORRECTIONS, newCorrectionRow);
  readonly snapshots = new Ring<SnapshotRow>(TRACE_SNAPSHOTS, newSnapshotRow);
  readonly session: TraceSession = { map: "", room: "", slot: -1, transport: "" };
  /** The `move_config_json` the predictor was created with. */
  moveConfigJson = "";

  /** A predictor was built for a map: drop the old rows and remember its move config. */
  begin(moveConfigJson: string): void {
    this.ticks.clear();
    this.corrections.clear();
    this.snapshots.clear();
    this.moveConfigJson = moveConfigJson;
  }

  setSession(map: string, room: string, slot: number, transport: TransportKind): void {
    this.session.map = map;
    this.session.room = room;
    this.session.slot = slot;
    this.session.transport = transport;
  }

  /** One predicted tick (`Predictor.step`): `cmd` is the command that was simulated, or, when `settled`, the one the server's earlier simulation of the tick made obsolete. */
  pushTick(nowMs: number, view: PlayerView, ev: MoveEventsData, cmd: InputCmdData, settled = false): void {
    fillTickRow(this.ticks.claim(), nowMs, view, ev, cmd, settled);
  }

  /** One snapshot reconciled with the prediction. */
  pushSnapshot(nowMs: number, tick: number, lead: number, slack: number | null, latestBefore: number, outcome: number, err: number): void {
    const row = this.snapshots.claim();
    row.t = nowMs;
    row.tick = tick;
    row.lead = lead;
    row.slack = slack;
    row.latestBefore = latestBefore;
    row.outcome = outcome;
    row.err = err;
  }

  /** A reconciliation moved the latest predicted state by `-(errX, errY, errZ)`. */
  pushCorrection(
    nowMs: number,
    tick: number,
    errX: number,
    errY: number,
    errZ: number,
    serverTick: number,
    ackTick: number,
    rttMs: number | null,
  ): void {
    const row = this.corrections.claim();
    row.tick = tick;
    row.t = nowMs;
    row.err[0] = errX;
    row.err[1] = errY;
    row.err[2] = errZ;
    row.serverTick = serverTick;
    row.ackTick = ackTick;
    row.rttMs = rttMs;
  }
}

// ---- Payload ----

export interface TickRecord {
  tick: number;
  t: number;
  pos: Vec3Tuple;
  vel: Vec3Tuple;
  speed: number;
  flags: TickFlags;
  input: TickInput;
  /** Names of the `MoveEventsData` booleans that were true this tick (empty when none). */
  events: string[];
  /** Speed into the ground (m/s) on a landing tick, else null. */
  landed: number | null;
  /** The tick was already confirmed by the server: nothing was simulated. */
  settled: boolean;
}

export interface TracePayload {
  version: typeof TRACE_VERSION;
  name: typeof TRACE_NAME;
  /** Ticks per second of every `tick` in the payload (`TICK_RATE`). */
  tickRate: number;
  map: string;
  room: string;
  slot: number;
  /** Which transport carried the session: "webtransport" or "websocket". */
  transport: TransportKind | "";
  /** ISO 8601. */
  capturedAt: string;
  /** The parsed `move_config_json` of the predictor, or the string itself when it is not JSON. */
  moveConfig: unknown;
  /** Oldest first. */
  ticks: TickRecord[];
  /** Oldest first, only the ones inside the window of `ticks`. */
  corrections: CorrectionRow[];
  /** Oldest first, one per snapshot inside the window of `ticks`. */
  snapshots: SnapshotRow[];
}

function parseMoveConfig(json: string): unknown {
  try {
    return JSON.parse(json) as unknown;
  } catch {
    return json;
  }
}

function tickRecord(r: TickRow): TickRecord {
  return {
    tick: r.tick,
    t: r.t,
    pos: [r.pos[0], r.pos[1], r.pos[2]],
    vel: [r.vel[0], r.vel[1], r.vel[2]],
    speed: r.speed,
    flags: { ...r.flags },
    input: { ...r.input },
    events: eventNames(r.eventBits),
    landed: r.landed,
    settled: r.settled,
  };
}

function correctionRecord(r: CorrectionRow): CorrectionRow {
  return { tick: r.tick, t: r.t, err: [r.err[0], r.err[1], r.err[2]], serverTick: r.serverTick, ackTick: r.ackTick, rttMs: r.rttMs };
}

/** Copies the rings into a payload that no longer changes while the recorder keeps writing. */
export function buildTracePayload(rec: TraceRecorder, capturedAt: Date): TracePayload {
  const ticks = rec.ticks;
  const since = ticks.length > 0 ? ticks.at(0).t : -Infinity;
  const corrections: CorrectionRow[] = [];
  for (let i = 0; i < rec.corrections.length; i++) {
    const c = rec.corrections.at(i);
    if (c.t >= since) corrections.push(correctionRecord(c));
  }
  const snapshots: SnapshotRow[] = [];
  for (let i = 0; i < rec.snapshots.length; i++) {
    const s = rec.snapshots.at(i);
    if (s.t >= since) snapshots.push({ ...s });
  }
  return {
    version: TRACE_VERSION,
    name: TRACE_NAME,
    tickRate: TICK_RATE,
    map: rec.session.map,
    room: rec.session.room,
    slot: rec.session.slot,
    transport: rec.session.transport,
    capturedAt: capturedAt.toISOString(),
    moveConfig: parseMoveConfig(rec.moveConfigJson),
    ticks: ticks.map(tickRecord),
    corrections,
    snapshots,
  };
}

// ---- Upload ----

export type TraceOutcome =
  | { kind: "saved"; file: string }
  /** 404: the server is not running with `--dev`. */
  | { kind: "needsDev" }
  | { kind: "failed"; message: string };

export interface TraceNotice {
  message: string;
  kind: "success" | "error";
}

/** `{ "file": "gunrun-traces/trace-....json", "bytes": n }`; anything else is shown as far as it is short text. */
async function savedFile(res: Response): Promise<string> {
  let text = "";
  try {
    text = (await res.text()).trim();
  } catch {
    return "(unnamed)";
  }
  if (text.startsWith("{")) {
    try {
      const body: unknown = JSON.parse(text);
      if (isRecord(body)) {
        for (const key of ["file", "path"]) {
          const v = body[key];
          if (typeof v === "string" && v !== "") return v;
        }
      }
    } catch {
      // Not JSON after all: fall through.
    }
    return "(unnamed)";
  }
  return text !== "" && text.length <= 200 ? text : "(unnamed)";
}

/** POSTs the payload to `/dev/trace` of the server the page came from. */
export async function postTrace(payload: TracePayload, doFetch: typeof fetch = fetch, url: string = TRACE_URL): Promise<TraceOutcome> {
  let res: Response;
  try {
    res = await doFetch(url, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(payload),
      credentials: "omit",
      signal: AbortSignal.timeout(POST_TIMEOUT_MS),
    });
  } catch {
    return { kind: "failed", message: "Trace failed: cannot reach the server" };
  }
  if (res.status === 404) return { kind: "needsDev" };
  if (!res.ok) return { kind: "failed", message: `Trace failed: HTTP ${res.status}` };
  return { kind: "saved", file: await savedFile(res) };
}

export function traceNotice(outcome: TraceOutcome): TraceNotice {
  switch (outcome.kind) {
    case "saved":
      return { message: `Trace saved: ${outcome.file}`, kind: "success" };
    case "needsDev":
      return { message: "Trace capture needs a --dev server", kind: "error" };
    case "failed":
      return { message: outcome.message, kind: "error" };
  }
}

/** F8: builds the payload from the recorder, uploads it and says what happened. Never throws. */
export async function captureTrace(rec: TraceRecorder, doFetch?: typeof fetch): Promise<TraceNotice> {
  try {
    return traceNotice(await postTrace(buildTracePayload(rec, new Date()), doFetch));
  } catch (e) {
    return { message: `Trace failed: ${e instanceof Error ? e.message : "unknown error"}`, kind: "error" };
  }
}
